/**
 * RightSidebarPanel - 右侧边栏面板
 * 
 * 包含：Assistance（AI Agent + Signal Assistant）与 Strategy 两个标签页
 */

import React, { useState } from 'react';
import { ThunderboltOutlined, BulbOutlined, DownOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import InteractiveChat from '../InteractiveChat';
import AgentChat from '../AgentChat';
import { StrategyBuilder } from '../StrategyBuilder';
import StrategyDashboard from '../StrategyBuilder/StrategyDashboard';
import { Strategy } from '../StrategyBuilder/types';

interface RightSidebarPanelProps {
  onSignalSelect?: (signal: any) => void;
  onStrategySelect?: (strategy: Strategy | null) => void;
  onTimeSelect?: (timestamp: number) => void;
  height?: number | string;
  // Agent 需要的 props
  chartApiRef?: React.RefObject<any>;
  ohlcData?: any[];
  currentSymbol?: string;
  currentTimeframe?: string;
  selectedPoint?: { time: number; price: number } | null;
  // Agent 回调
  onPointSelect?: (point: { index: number; time: number; price: number } | null) => void;
  onShowAnalysisPanel?: (panel: 'point' | 'rectangle') => void;
  onPointAnalysisAutoRun?: () => void;
  onRectangleAnalysisAutoRun?: () => void;
  onSetIndicators?: (args: {
    selected?: string[];
    mode?: 'replace' | 'add' | 'remove';
    placements?: Record<string, 'main' | 'pane'>;
    open_panel?: boolean;
  }) => { success: boolean; result?: any; error?: string };
  onSetChartContext?: (args: {
    asset_type?: 'crypto' | 'stocks';
    symbol?: string;
    timeframe?: string;
    normalize?: boolean;
    reason?: string;
  }) => { success: boolean; result?: any; error?: string };
  onRectangleAnalysisResult?: (result: any) => void;
  // 回调：关闭指标面板（Agent 设置指标后自动关闭）
  onCloseIndicatorPanel?: () => void;
}

const RightSidebarPanel: React.FC<RightSidebarPanelProps> = ({ 
  onSignalSelect, 
  onStrategySelect,
  onTimeSelect,
  height = 770,
  chartApiRef,
  ohlcData,
  currentSymbol,
  currentTimeframe,
  selectedPoint,
  onPointSelect,
  onShowAnalysisPanel,
  onPointAnalysisAutoRun,
  onRectangleAnalysisAutoRun,
  onSetIndicators,
  onSetChartContext,
  onRectangleAnalysisResult,
  onCloseIndicatorPanel,
}) => {
  const { t } = useTranslation();
  const { theme, themeName } = useAppTheme();
  const isDarkMode = themeName === 'dark';
  const [activeTab, setActiveTab] = useState<'assistance' | 'strategy'>('assistance');
  const [assistanceMode, setAssistanceMode] = useState<'agent' | 'signal'>('agent');
  const [strategyViewMode, setStrategyViewMode] = useState<'dashboard' | 'builder'>('dashboard');

  const hideTabBar = activeTab === 'strategy' && strategyViewMode === 'builder';
  const contentHeight = hideTabBar 
    ? '100%' 
    : (typeof height === 'number' ? height - 50 : `calc(${height} - 50px)`);

  const getTabStyle = (isActive: boolean) => ({
    flex: 1,
    padding: '8px 12px',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    fontSize: 12,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    background: isActive 
      ? (isDarkMode ? theme.toolbarBackground : '#fff') 
      : 'transparent',
    color: isActive ? theme.candleUp : theme.textSecondary,
    boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
  });

  return (
    <div
      style={{
        height: height,
        display: 'flex',
        flexDirection: 'column',
        background: theme.surfaceBackground,
        borderRadius: 8,
        border: `1px solid ${theme.surfaceBorder}`,
        overflow: 'hidden'
      }}
    >
      {/* Tab Bar */}
      {!hideTabBar && (
        <div style={{
          padding: '10px 12px',
          borderBottom: `1px solid ${theme.surfaceBorder}`,
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            background: isDarkMode ? theme.surfaceBorder : '#f0f0f0',
            borderRadius: 8,
            padding: 3,
            gap: 3
          }}>
            <button onClick={() => setActiveTab('assistance')} style={getTabStyle(activeTab === 'assistance')}>
              <BulbOutlined />
              {t('sidebar.assistance', 'Assistance')}
            </button>
            <button onClick={() => setActiveTab('strategy')} style={getTabStyle(activeTab === 'strategy')}>
              <ThunderboltOutlined />
              {t('strategyBuilder.strategy', 'Strategy')}
            </button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div style={{ flex: 1, overflow: 'hidden', height: contentHeight }}>
        {activeTab === 'assistance' ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Assistance mode selector (AI Agent / Signal Assistant) */}
            <div style={{
              padding: '10px 12px',
              borderBottom: `1px solid ${theme.surfaceBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'signal', label: t('chat.signalAssistant', 'Signal Analysis Assistant') },
                    { key: 'agent', label: t('agent.tab', 'AI Agent') },
                  ],
                  onClick: ({ key }) => setAssistanceMode(key as 'agent' | 'signal'),
                }}
              >
                <div style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: theme.textSecondary,
                  textTransform: 'uppercase',
                }}>
                  <span>
                    {assistanceMode === 'agent'
                      ? t('agent.tab', 'AI Agent')
                      : t('chat.signalAssistant', 'Signal Analysis Assistant')}
                  </span>
                  <DownOutlined style={{ fontSize: 10, opacity: 0.75 }} />
                </div>
              </Dropdown>
            </div>

            <div style={{ flex: 1, overflow: 'hidden' }}>
              {assistanceMode === 'agent' ? (
          <AgentChat 
            height="100%"
            chartApiRef={chartApiRef}
            ohlcData={ohlcData}
            currentSymbol={currentSymbol}
            currentTimeframe={currentTimeframe}
            selectedPoint={selectedPoint}
            onPointSelect={onPointSelect}
            onShowAnalysisPanel={onShowAnalysisPanel}
            onPointAnalysisAutoRun={onPointAnalysisAutoRun}
            onRectangleAnalysisAutoRun={onRectangleAnalysisAutoRun}
            onSetIndicators={onSetIndicators}
            onSetChartContext={onSetChartContext}
            onRectangleAnalysisResult={onRectangleAnalysisResult}
            onCloseIndicatorPanel={onCloseIndicatorPanel}
          />
              ) : (
                <InteractiveChat onSignalSelect={onSignalSelect} height="100%" />
              )}
            </div>
          </div>
        ) : (
          strategyViewMode === 'dashboard' ? (
            <StrategyDashboard 
              onBuildStrategy={() => setStrategyViewMode('builder')}
              onStrategySelect={onStrategySelect}
              onTimeSelect={onTimeSelect}
            />
          ) : (
            <StrategyBuilder 
              showBackButton={true}
              onBack={() => setStrategyViewMode('dashboard')}
              onSaveComplete={() => setStrategyViewMode('dashboard')}
            />
          )
        )}
      </div>
    </div>
  );
};

export default RightSidebarPanel;
